import { Controller } from '@hotwired/stimulus'
import { disableScroll, enableScroll } from '../utils/global'
import { observeBodyScrollDirection } from './scroll_direction_controller'

// Контроллер для header:
// Управление popup меню (открытие/закрытие, блокировка скролла)
// CSS переменные --header-height обновляются этим HeaderController
export default class HeaderController extends Controller {
  static targets = ['burger', 'popupMenu']

  resizeObserver = null
  matchMedia = window.matchMedia('(max-width: 767px)')
  isScrollDisabled = false
  stopObservingScrollDirection = null
  onLightAtTop = false

  connect() {
    this.boundOnKeydown = this.onKeydown.bind(this)
    this.boundClick = this.onClick.bind(this)
    this.boundOnViewportChange = this.onViewportChange.bind(this)
    this.boundOnScroll = this.onScroll.bind(this)
    this.isOpen = false
    this.onLightAtTop = this.element.classList.contains('header--on-light')

    if (this.hasPopupMenuTarget) {
      this.popupMenuTarget.setAttribute('aria-hidden', 'true')
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateHeight()
    })
    this.resizeObserver.observe(this.element)

    this.matchMedia.addEventListener('change', this.boundOnViewportChange)
    window.addEventListener('scroll', this.boundOnScroll, { passive: true })
    this.stopObservingScrollDirection = observeBodyScrollDirection(
      this.onScrollDirectionChange.bind(this)
    )

    requestAnimationFrame(() => {
      this.updateHeight()
      this.updateColorScheme()
    })
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundOnKeydown)
    document.removeEventListener('click', this.boundClick)
    this.matchMedia.removeEventListener('change', this.boundOnViewportChange)
    window.removeEventListener('scroll', this.boundOnScroll)
    this.stopObservingScrollDirection?.()
    this.stopObservingScrollDirection = null
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
  }

  toggle(event) {
    event?.preventDefault()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.isOpen = true
    this.burgerTarget.setAttribute('aria-expanded', 'true')
    this.burgerTarget.setAttribute('aria-label', 'Закрыть меню')

    document.addEventListener('keydown', this.boundOnKeydown)
    document.addEventListener('click', this.boundClick)

    if (this.hasPopupMenuTarget) {
      this.popupMenuTarget.setAttribute('aria-hidden', 'false')
    }

    if (this.matchMedia.matches) {
      disableScroll()
      this.isScrollDisabled = true
    }
  }

  close() {
    this.isOpen = false
    this.burgerTarget.setAttribute('aria-expanded', 'false')
    this.burgerTarget.setAttribute('aria-label', 'Открыть меню')

    document.removeEventListener('keydown', this.boundOnKeydown)
    document.removeEventListener('click', this.boundClick)

    if (this.hasPopupMenuTarget) {
      this.popupMenuTarget.setAttribute('aria-hidden', 'true')
    }

    if (this.isScrollDisabled) {
      enableScroll()
      this.isScrollDisabled = false
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  onClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }

  onViewportChange() {
    this.close()
  }

  onScrollDirectionChange(direction) {
    if (this.isOpen && !this.matchMedia.matches && direction === 'down') {
      this.close()
    }
  }

  onScroll() {
    this.updateColorScheme()
  }

  updateColorScheme() {
    this.element.classList.toggle(
      'header--on-light',
      this.onLightAtTop || window.scrollY > 0
    )
  }

  updateHeight() {
    document.documentElement.style.setProperty(
      '--header-height',
      `${this.element.offsetHeight}px`
    )
  }
}
